import { useAuthService } from '@/providers/auth-service-provider';
import { createContext, useCallback, useContext, useMemo, useState } from 'react';

const ORGANIZATION_ID_STORAGE_KEY = (userId: string) => `portfolio-manager.organization-id.${userId}`;

const organizationIdStorage = {
  read(userId: string): string | null {
    try {
      return window.localStorage.getItem(ORGANIZATION_ID_STORAGE_KEY(userId));
    } catch {
      return null;
    }
  },
  write(userId: string, organizationId: string): void {
    try {
      window.localStorage.setItem(ORGANIZATION_ID_STORAGE_KEY(userId), organizationId);
    } catch {
      return;
    }
  },
  clear(userId: string): void {
    try {
      window.localStorage.removeItem(ORGANIZATION_ID_STORAGE_KEY(userId));
    } catch {
      return;
    }
  },
};

type OrganizationServiceState =
  | {
      hasOrganizationContext: true;
      organizationId: string;
    }
  | {
      hasOrganizationContext: false;
      organizationId: null;
    };

export type OrganizationService = OrganizationServiceState & {
  setContext: (organizationId: string) => void;
  clearContext: () => void;
};

const OrganizationServiceContext = createContext<OrganizationService | null>(null);

function readOrganizationId(userId: string | null): string | null {
  return userId === null ? null : organizationIdStorage.read(userId);
}

export function OrganizationServiceProvider({ children }: React.PropsWithChildren) {
  const authService = useAuthService();
  const userId = authService.getUserId();

  const [activeUserId, setActiveUserId] = useState<string | null>(userId);
  const [organizationId, setOrganizationId] = useState<string | null>(() => readOrganizationId(userId));

  if (activeUserId !== userId) {
    setActiveUserId(userId);
    setOrganizationId(readOrganizationId(userId));
  }

  const setContext = useCallback(
    (organizationId: string) => {
      if (!userId) return;
      organizationIdStorage.write(userId, organizationId);
      setOrganizationId(organizationId);
    },
    [userId],
  );

  const clearContext = useCallback(() => {
    if (userId) organizationIdStorage.clear(userId);
    setOrganizationId(null);
  }, [userId]);

  const value: OrganizationService = useMemo(
    () =>
      organizationId === null
        ? { hasOrganizationContext: false, organizationId: null, setContext, clearContext }
        : { hasOrganizationContext: true, organizationId: organizationId, setContext, clearContext },
    [organizationId, setContext, clearContext],
  );

  return <OrganizationServiceContext.Provider value={value}>{children}</OrganizationServiceContext.Provider>;
}

export function useOrganizationService(): OrganizationService {
  const context = useContext(OrganizationServiceContext);

  if (context === null) {
    throw new Error('useOrganizationService must be used within a <OrganizationServiceProvider>.');
  }

  return context;
}
